import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';

interface NavigationItem {
  name: string;
  href: string;
  active?: boolean;
}

interface HeaderProps {
  navigation: {
    main: NavigationItem[];
    secondary?: NavigationItem[];
  };
  search?: {
    enabled: boolean;
    placeholder: string;
  };
  companyName: string;
}

export function Header({ navigation, search, companyName }: HeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Search functionality to be implemented
    console.log('Search:', searchQuery);
  };

  return (
    <>
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link href="https://fonts.googleapis.com/css2?family=Lato:wght@400;700&family=Open+Sans:wght@400;600;700&display=swap" rel="stylesheet" />
      </Helmet>
      
      <div id="main-wrap">
        <div id="banner-wrap">
          <div id="header-wrap">
            <header 
              id="header" 
              className="sticky top-0 z-50 bg-white shadow-sm border-b border-gray-200"
            >
              <div className="max-w-7xl mx-auto">
                <table className="w-full">
                  <tbody>
                    <tr>
                      {/* Desktop Navigation */}
                      <td id="nav-wrap" className="hidden md:table-cell">
                        <nav>
                          <ul className="flex space-x-8">
                            {navigation.main.map((item) => (
                              <li key={item.href} className={item.active ? 'active' : ''}>
                                <a 
                                  href={item.href}
                                  className={`
                                    font-body text-nav text-primary-text hover:text-gray-900 
                                    transition-colors duration-200 py-4 px-2 block
                                    ${item.active ? 'text-gray-900 font-semibold' : ''}
                                  `}
                                >
                                  {item.name}
                                </a>
                              </li>
                            ))}
                          </ul>
                        </nav>
                      </td>
                      
                      {/* Search */}
                      {search?.enabled && (
                        <td className="hidden md:table-cell text-right">
                          <span>
                            <form 
                              id="wsite-header-search-form" 
                              onSubmit={handleSearch}
                              className="inline-block"
                            >
                              <input
                                type="text"
                                name="q"
                                placeholder={search.placeholder}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="
                                  px-3 py-2 border border-gray-200 rounded-md 
                                  text-sm focus:outline-none focus:ring-2 
                                  focus:ring-primary focus:border-transparent
                                  bg-white placeholder-gray-400
                                "
                              />
                            </form>
                          </span>
                        </td>
                      )}
                      
                      {/* Mobile menu button */}
                      <td className="md:hidden text-right">
                        <button
                          id="nav-trigger"
                          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                          className="p-2 text-primary-text hover:text-gray-900"
                          aria-label="Toggle navigation menu"
                        >
                          <svg 
                            className="w-6 h-6" 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            {isMobileMenuOpen ? (
                              <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                strokeWidth={2} 
                                d="M6 18L18 6M6 6l12 12" 
                              />
                            ) : (
                              <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                strokeWidth={2} 
                                d="M4 6h16M4 12h16M4 18h16" 
                              />
                            )}
                          </svg>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
                
                {/* Secondary Navigation (dropdown/submenu) */}
                {navigation.secondary && navigation.secondary.length > 0 && (
                  <div id="wsite-menus" className="hidden md:block border-t border-gray-100">
                    <div className="py-2">
                      <ul className="flex space-x-6">
                        {navigation.secondary.map((item) => (
                          <li key={item.href}>
                            <a 
                              href={item.href}
                              className="
                                font-body text-sm text-primary-text hover:text-gray-900 
                                transition-colors duration-200 py-2 px-1 block
                              "
                            >
                              <span>{item.name}</span>
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </header>
          </div>
          
          {/* Company Name Banner */}
          <div id="banner" className="bg-gray-50 border-b border-gray-200">
            <div id="banner-inner" className="max-w-7xl mx-auto py-6 px-4">
              <span className="block">
                <span className="block">
                  <span className="font-display text-display text-gray-900">
                    {companyName}
                  </span>
                </span>
              </span>
              <h2 className="sr-only">Watson Aero LLC - Aircraft Engine Overhaul Services</h2>
            </div>
          </div>
        </div>
        
        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div 
            id="navmobile" 
            className="md:hidden bg-white border-b border-gray-200 shadow-lg"
          >
            {/* Mobile Search */}
            {search?.enabled && (
              <div className="px-4 py-3 border-b border-gray-100">
                <form onSubmit={handleSearch}>
                  <input
                    type="text"
                    name="q"
                    placeholder={search.placeholder}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="
                      w-full px-3 py-2 border border-gray-200 rounded-md 
                      text-sm focus:outline-none focus:ring-2 
                      focus:ring-primary focus:border-transparent
                      bg-white placeholder-gray-400
                    "
                  />
                </form>
              </div>
            )}
            
            {/* Mobile Main Navigation */}
            <div className="py-2">
              <ul className="divide-y divide-gray-100">
                {navigation.main.map((item) => (
                  <li key={item.href} className={item.active ? 'active' : ''}>
                    <a 
                      href={item.href}
                      className={`
                        block px-4 py-3 font-body text-primary-text hover:text-gray-900 
                        hover:bg-gray-50 transition-colors duration-200
                        ${item.active ? 'text-gray-900 font-semibold bg-gray-50' : ''}
                      `}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.name}
                    </a>
                  </li>
                ))}
              </ul>
              
              {/* Mobile Secondary Navigation */}
              {navigation.secondary && navigation.secondary.length > 0 && (
                <div className="border-t border-gray-100 mt-2 pt-2">
                  <ul className="divide-y divide-gray-100">
                    {navigation.secondary.map((item) => (
                      <li key={item.href}>
                        <a 
                          href={item.href}
                          className="
                            block px-4 py-3 font-body text-sm text-primary-text hover:text-gray-900 
                            hover:bg-gray-50 transition-colors duration-200
                          "
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          <span>{item.name}</span>
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}